import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import CourseNameBadge from '../../../components/common/CourseNameBadge';

// ─── Constants ─────────────────────────────────────────────────────────────────

const TYPES = [
  { value:'general',  label:'General',  color:'primary',   icon:'bi-megaphone'      },
  { value:'urgent',   label:'Urgent',   color:'danger',    icon:'bi-exclamation-triangle' },
  { value:'event',    label:'Event',    color:'success',   icon:'bi-calendar-event' },
  { value:'holiday',  label:'Holiday',  color:'warning',   icon:'bi-umbrella'       },
  { value:'reminder', label:'Reminder', color:'info',      icon:'bi-bell'           },
];

const AUDIENCES = [
  { value:'all',      label:'Everyone'    },
  { value:'students', label:'Students'    },
  { value:'staff',    label:'Staff'       },
  { value:'batch',    label:'Specific Batch'  },
  { value:'course',   label:'Specific Course' },
];

const PRIORITIES = [
  { value:'low',    label:'Low',    color:'secondary' },
  { value:'medium', label:'Medium', color:'warning'   },
  { value:'high',   label:'High',   color:'danger'    },
];

const typeColor = (t) => TYPES.find(x=>x.value===t)?.color||'secondary';
const typeIcon  = (t) => TYPES.find(x=>x.value===t)?.icon||'bi-megaphone';
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

// ─── Main Component ─────────────────────────────────────────────────────────────

const blankForm = { title:'', content:'', type:'general', priority:'medium', audience:'all', batchId:'', courseId:'', isPinned:false, expiresAt:'' };

export default function Announcements() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canWrite = ['admin','manager','team_lead'].includes(role);
  const canAdmin = ['admin'].includes(role);

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [courses, setCourses]             = useState([]);
  const [batches, setBatches]             = useState([]);
  const [page, setPage]                   = useState(1);
  const [totalPages, setTotalPages]       = useState(1);

  const [showForm, setShowForm]           = useState(false);
  const [editTarget, setEditTarget]       = useState(null);
  const [form, setForm]                   = useState(blankForm);
  const [saving, setSaving]               = useState(false);

  const [alert, setAlert]                 = useState({ show:false, msg:'', type:'success' });
  const showAlert = (msg,type='success') => setAlert({ show:true, msg, type });

  useEffect(() => {
    api.get('/training/courses', {params:{limit:200}}).then(r=>setCourses(r.data?.data||r.data?.records||[])).catch(()=>{});
    api.get('/training/batches', {params:{limit:200}}).then(r=>setBatches(r.data?.records||r.data?.data||[])).catch(()=>{});
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      // Admins/managers/team_leads see everything they've posted, including expired
      // ones, so they can edit/delete them. Other users see only currently-live ones.
      const params = { page, limit: 20 };
      if (canWrite) params.includeExpired = true;
      const r = await api.get('/training/announcements', { params });
      setAnnouncements(r.data?.records||r.data?.data||[]);
      setTotalPages(r.data?.totalPages||1);
    } catch(_){}
    setLoading(false);
  }, [page, canWrite]);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const openCreate = () => { setForm(blankForm); setEditTarget(null); setShowForm(true); };
  const openEdit   = (a) => {
    setForm({ title:a.title, content:a.content, type:a.type, priority:a.priority||'medium', audience:a.audience, batchId:a.batch?._id||'', courseId:a.course?._id||'', isPinned:a.isPinned||false, expiresAt:a.expiresAt?new Date(a.expiresAt).toISOString().split('T')[0]:'' });
    setEditTarget(a);
    setShowForm(true);
  };

  const handleSave = async () => {
    if(!form.title.trim()||!form.content.trim()){ showAlert('Title and content are required.','danger'); return; }
    setSaving(true);
    try {
      let r;
      if(editTarget) r = await api.put(`/training/announcements/${editTarget._id}`, form);
      else           r = await api.post('/training/announcements', form);
      if(r.data?.success!==false){
        showAlert(editTarget?'Updated.':'Announcement posted.','success');
        setShowForm(false);
        // Jump to page 1 — new announcement sorts to the top.
        if (!editTarget && page !== 1) setPage(1);
        else fetchAnnouncements();
      }
      else showAlert(r.data?.message||'Failed.','danger');
    } catch(e){ showAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };

  const handleDelete = async (a) => {
    if(!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/training/announcements/${a._id}`);
      showAlert('Deleted.','success');
      fetchAnnouncements();
    } catch(e){ showAlert(e.response?.data?.message||'Failed.','danger'); }
  };

  const handleMarkRead = async (a) => {
    try {
      await api.post(`/training/announcements/${a._id}/read`, {});
      setAnnouncements(prev => prev.map(x => x._id===a._id ? {...x, isRead:true} : x));
    } catch(_){}
  };

  const filteredBatches = form.courseId ? batches.filter(b=>b.course?._id===form.courseId||b.courseId===form.courseId) : batches;

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={()=>setAlert(p=>({...p,show:false}))} />

      <PageHeader
        title="Announcements"
        icon="bi-megaphone"
        subtitle="Post and manage announcements for students and staff"
        actions={canWrite && (
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <i className="bi bi-plus-lg me-1"/>New Announcement
          </button>
        )}
      />

      {/* Create/Edit Form */}
      {showForm && (
        <div className="card border-primary mb-3">
          <div className="card-header bg-primary text-white d-flex justify-content-between">
            <span>{editTarget?'Edit Announcement':'New Announcement'}</span>
            <button className="btn-close btn-close-white btn-sm" onClick={()=>setShowForm(false)}/>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Title <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={form.title} onChange={e=>setF('title',e.target.value)} />
              </div>
              <div className="col-md-2">
                <label className="form-label">Type</label>
                <select className="form-select form-select-sm" value={form.type} onChange={e=>setF('type',e.target.value)}>
                  {TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Priority</label>
                <select className="form-select form-select-sm" value={form.priority} onChange={e=>setF('priority',e.target.value)}>
                  {PRIORITIES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Audience</label>
                <select className="form-select form-select-sm" value={form.audience} onChange={e=>setF('audience',e.target.value)}>
                  {AUDIENCES.map(a=><option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>

              {form.audience === 'batch' && (
                <>
                  <div className="col-md-4">
                    <label className="form-label">Course</label>
                    <select className="form-select form-select-sm" value={form.courseId} onChange={e=>{setF('courseId',e.target.value);setF('batchId','');}}>
                      <option value="">Select Course</option>
                      {courses.map(c=><option key={c._id} value={c._id}>{c.courseName || c.name || c.courseCode || '(unnamed)'}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Batch</label>
                    <select className="form-select form-select-sm" value={form.batchId} onChange={e=>setF('batchId',e.target.value)}>
                      <option value="">Select Batch</option>
                      {filteredBatches.map(b=><option key={b._id} value={b._id}>{b.batchNumber || '(unnamed)'}</option>)}
                    </select>
                  </div>
                </>
              )}

              {form.audience === 'course' && (
                <div className="col-md-4">
                  <label className="form-label">Course</label>
                  <select className="form-select form-select-sm" value={form.courseId} onChange={e=>setF('courseId',e.target.value)}>
                    <option value="">Select Course</option>
                    {courses.map(c=><option key={c._id} value={c._id}>{c.courseName || c.name || c.courseCode || '(unnamed)'}</option>)}
                  </select>
                </div>
              )}

              <div className="col-md-3">
                <label className="form-label">Expires On (optional)</label>
                <input className="form-control form-control-sm" type="date" value={form.expiresAt} onChange={e=>setF('expiresAt',e.target.value)} />
              </div>
              <div className="col-md-9 d-flex align-items-end">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="isPinned" checked={form.isPinned} onChange={e=>setF('isPinned',e.target.checked)} />
                  <label className="form-check-label" htmlFor="isPinned">Pin this announcement</label>
                </div>
              </div>

              <div className="col-12">
                <label className="form-label">Content <span className="text-danger">*</span></label>
                <textarea className="form-control" rows={4} value={form.content} onChange={e=>setF('content',e.target.value)} />
              </div>
            </div>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <button className="btn btn-secondary btn-sm" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Saving…</>:(editTarget?'Save Changes':'Post Announcement')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements list */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"/></div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-5 text-muted"><i className="bi bi-megaphone fs-1 d-block mb-2"/>No announcements yet.</div>
      ) : (
        announcements.map(a => (
          <div key={a._id} className={`card mb-2 border-${typeColor(a.type)} ${a.isRead ? 'opacity-75' : ''}`}>
            <div className="card-body py-2 px-3">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {a.isPinned && <i className="bi bi-pin-fill text-primary" title="Pinned"/>}
                  <i className={`bi ${typeIcon(a.type)} text-${typeColor(a.type)}`}/>
                  <span className="fw-semibold">{a.title}</span>
                  <span className={`badge bg-${typeColor(a.type)}`}>{a.type}</span>
                  {a.priority && a.priority !== 'medium' && <span className={`badge bg-${PRIORITIES.find(p=>p.value===a.priority)?.color||'secondary'}`}>{a.priority}</span>}
                  {a.batch && <span className="badge bg-light text-dark border small">{a.batch.batchNumber}</span>}
                  {a.course && <CourseNameBadge courseName={a.course.courseName || a.course.name || a.course.courseCode} courseCode={a.course.courseCode || ''} courseColor={a.course.courseColor} compact />}
                  <span className="badge bg-secondary small">{AUDIENCES.find(x=>x.value===a.audience)?.label}</span>
                  {!a.isRead && <span className="badge bg-primary small">New</span>}
                </div>
                <div className="d-flex gap-1 align-items-center">
                  <small className="text-muted">{fmtDate(a.createdAt)}</small>
                  {!a.isRead && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={()=>handleMarkRead(a)} title="Mark as read">
                      <i className="bi bi-check"/>
                    </button>
                  )}
                  {canWrite && (
                    <button className="btn btn-outline-primary btn-sm" onClick={()=>openEdit(a)}>
                      <i className="bi bi-pencil"/>
                    </button>
                  )}
                  {canAdmin && (
                    <button className="btn btn-outline-danger btn-sm" onClick={()=>handleDelete(a)}>
                      <i className="bi bi-trash"/>
                    </button>
                  )}
                </div>
              </div>
              <p className="mb-0 mt-1 small">{a.content}</p>
              <div className="d-flex gap-3 mt-1 small text-muted">
                <span>By {a.createdBy?.firstName} {a.createdBy?.lastName}</span>
                {a.expiresAt && <span>Expires: {fmtDate(a.expiresAt)}</span>}
                <span className="ms-auto">{a.readBy?.length||0} read</span>
              </div>
            </div>
          </div>
        ))
      )}

      {totalPages > 1 && (
        <nav className="mt-3 d-flex justify-content-center">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page<=1?'disabled':''}`}><button className="page-link" onClick={()=>setPage(p=>Math.max(1,p-1))}>‹</button></li>
            {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
              <li key={p} className={`page-item ${p===page?'active':''}`}><button className="page-link" onClick={()=>setPage(p)}>{p}</button></li>
            ))}
            <li className={`page-item ${page>=totalPages?'disabled':''}`}><button className="page-link" onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>›</button></li>
          </ul>
        </nav>
      )}
    </div>
  );
}
